import React from 'react'
import classNames from 'classnames'
import Icon from '../Icon'
import { twMerge } from 'tailwind-merge'

export const Button = React.forwardRef(({
  children,
  link = false,
  inline = false,
  inverted = false,
  tertiary = false,

  className = '',
  disabled = false,
  size = 'base',
  iconPosition = 'left',
  
  state = 'primary',
  variant = 'solid',
  ...props
}, ref) => {
  let buttonClassNames = []

  const solidStateClasses = {
    primary: ['bg-primary border-primary text-white', 'hover:bg-white hover:text-primary'],
    secondary: ['bg-secondary border-secondary text-white', 'hover:bg-white hover:text-secondary'],
    tertiary: ['bg-tertiary border-tertiary text-white', 'hover:bg-white hover:text-tertiary'],
    positive: ['bg-positive border-positive text-white', 'hover:bg-white hover:text-positive'],
    negative: ['bg-negative border-negative text-white', 'hover:bg-white hover:text-negative'],
    warning: ['bg-warning border-warning text-white', 'hover:bg-white hover:text-warning'],
  } [state];

  const plainStateClasses = {
    primary: ['bg-white border-white text-primary', 'hover:bg-primary hover:text-white hover:border-primary'],
    secondary: ['bg-white border-white text-secondary', 'hover:bg-secondary hover:text-white hover:border-secondary'],
    tertiary: ['bg-white border-white text-tertiary', 'hover:bg-tertiary hover:text-white hover:border-tertiary'],
    positive: ['bg-white border-white text-positive', 'hover:bg-positive hover:text-white hover:border-positive'],
    negative: ['bg-white border-white text-negative', 'hover:bg-negative hover:text-white hover:border-negative'],
    warning: ['bg-white border-white text-warning', 'hover:bg-warning hover:text-white hover:border-warning'],
  } [state];

  const oultineStateClasses = {
    primary: ['border-primary text-primary', 'hover:bg-primary hover:text-white'],
    secondary: ['border-secondary text-secondary', 'hover:bg-secondary hover:text-white'],
    tertiary: ['border-tertiary text-tertiary', 'hover:bg-tertiary hover:text-white'],
    positive: ['border-positive text-positive', 'hover:bg-positive hover:text-white'],
    negative: ['border-negative text-negative', 'hover:bg-negative hover:text-white'],
    warning: ['border-warning text-warning', 'hover:bg-warning hover:text-white'],
  } [state];

  const textStateClasses = {
    primary: ['border-transparent text-primary', 'hover:bg-primary/75 hover:border-primary/75'], // TODO: investigate opacity on custom colour
    secondary: ['border-transparent text-secondary', 'hover:bg-secondary/75 hover:border-secondary/75'],
    tertiary: ['border-transparent text-tertiary', 'hover:bg-tertiary/75 hover:border-tertiary/75'],
    positive: ['border-transparent text-positive', 'hover:bg-positive/75 hover:border-positive/75'],
    negative: ['border-transparent text-negative', 'hover:bg-negative/75 hover:border-negative/75'],
    warning: ['border-transparent text-warning', 'hover:bg-warning/75 hover:border-warning/75'],
  } [state];

  const inlineStateClasses = {
    primary: ['text-primary', 'hover:text-primary-darker'],
    secondary: ['text-secondary', 'hover:text-secondary-darker'],
    tertiary: ['text-tertiary', 'hover:text-tertiary-darker'],
    positive: ['text-positive', 'hover:text-positive-darker'],
    negative: ['text-negative', 'hover:text-negative-darker'],
    warning: ['text-warning', 'hover:text-warning-darker'],
  } [state];

  const [variantClasses, variantHoverClasses] = {
    solid: solidStateClasses,
    outline: oultineStateClasses,
    text: textStateClasses,
    plain: plainStateClasses,
    inline: inlineStateClasses,
  } [variant]

  buttonClassNames.push(variantClasses);
  if (disabled) {
    buttonClassNames.push('cursor-not-allowed opacity-50')
  } else {
    buttonClassNames.push(variantHoverClasses);
  }

  if (!['text', 'inline'].includes(variant)) {
    buttonClassNames.push('shadow-sm');
  }

  if (variant !== 'inline') {
    buttonClassNames.push({
      base: 'py-3 px-6 text-base',
      sm: 'py-2 px-3 text-sm',
      xs: 'py-1 px-2 text-xs',
    }[size])
    buttonClassNames.push('border-2');
  }

  const iconClasses = [
    'text-center',
    'justify-self-center',
    'self-center'
  ]
  if (children) {
    buttonClassNames.push(variant === 'inline' ? 'space-x-1' : 'space-x-2')
  }

  let buttonBaseClasses = [
    'whitespace-nowrap',
    'flex-nowrap',
    'cursor-pointer',
    'inline-block',
    'font-medium',
    'text-center',
    'focus:outline-none',
    'focus:ring-2',
    'focus:ring-offset-2',
    'focus:ring-primary-darker',
    'transition',
    'duration-200',
    'ease-in-out',
    'rounded-sm',
    'whitespace-nowrap',
    'overflow-hidden',
    'text-ellipsis'
  ]

  const components = [
    <Icon key="icon" icon={props.icon} className={classNames(iconClasses)} />,
  ];

  if (children) {
    components.push(<span key="label">{ children }</span>);
  }

  if (iconPosition === 'right') {
    components.reverse();
  }

  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled}
      className={classNames(twMerge(buttonBaseClasses.concat(buttonClassNames).map((c) => c.split(' ')).flat(), className))}
      {...props}
    >
     { components }
    </button>
  )
})

export default Button
